<template>
    <data-table
        :columns="columns"
        :items="items"
        :no-items-view="{ noItems: trans('references.overview.results.empty') }"
        :pagination-container="paginationContainer"
        :query-params="queryParams"
        :selection-enabled="selectionEnabled"
        @row-clicked="showTaskDetails"
    >
        <template #task="{ item }">
            <td class="align-middle">
                <div class="flex h-full flex-grow items-center">
                    <task-group-badge
                        :bg-color="getTaskResolutionColor(item.resolution?.value) || getTaskGroupColor(item.group)"
                        :group="item.group"
                    />
                </div>
            </td>
        </template>

        <template #result="{ item }">
            <td class="align-middle">
                <div :class="getTaskResolutionColor(item.resolution?.value, 'text')" class="font-medium">
                    {{ item.resolution?.label }}
                </div>
            </td>
        </template>

        <template #date="{ item }">
            <td class="align-middle">
                <div class="text-nowrap">
                    {{ $filters.fullDate(item?.date, currentLocale) }}
                </div>
                <div class="small text-muted">{{ $filters.time(item?.date, currentLocale) }}</div>
            </td>
        </template>

        <template #title="{ item }">
            <td class="align-middle" style="max-width: 200px">
                <div class="truncate">{{ item.title }}</div>
                <div class="small truncate underline">{{ item.code }}</div>
            </td>
        </template>

        <template #asset="{ item }">
            <td class="align-middle" style="max-width: 200px">
                <div class="truncate">{{ item.asset?.name }}</div>
                <div v-if="item.asset?.manufacturer" class="small text-muted">{{ item.asset?.manufacturer }}</div>
                <div class="small truncate text-muted">Curie ID: {{ item.asset?.curieId || '-' }}</div>
            </td>
        </template>

        <template #location="{ item }">
            <td class="align-middle">
                <div class="truncate">{{ getAirportSubareaAndArea(item) }}</div>
                <div class="truncate">{{ getAirportAndSpecificLocation(item) }}</div>
            </td>
        </template>

        <template #report="{ item }">
            <td class="align-middle">
                <div class="line-clamp-2">{{ item.description }}</div>
            </td>
        </template>

        <template #user="{ item }">
            <td class="align-middle">
                <user-information v-if="item?.user" :info="item.user" />
                <span v-else>-</span>
            </td>
        </template>

        <template #header-presence>
            <span :title="trans('test_records.overview.cell.qr_code_scanned')">
                <qr-code class="h-5 w-5" />
            </span>
        </template>

        <template #presence="{ item }">
            <td class="text-center align-middle">
                <div>
                    <icon
                        v-if="item.presenceDetection"
                        v-c-tooltip="{
                            content: trans('test_records.overview.tooltip.qr_code_scanned'),
                        }"
                        icon="cil-check-alt"
                        size="lg"
                    />
                    <icon
                        v-else
                        v-c-tooltip="{
                            content: trans('test_records.overview.tooltip.qr_code_not_scanned'),
                        }"
                        icon="cil-x"
                        size="lg"
                    />
                </div>
            </td>
        </template>

        <template #header-attachments>
            <span :title="trans('test_records.overview.cell.attachments')">
                <icon name="cil-file" />
            </span>
        </template>

        <template #attachments="{ item }">
            <td class="text-center align-middle">
                <div>
                    <icon
                        v-if="item.attachmentsCount > 0"
                        v-c-tooltip="{
                            content: trans('general.attachments.count', { '%d': item.attachmentsCount }),
                        }"
                        :alt="trans('general.attachments.count', { '%d': item.attachmentsCount })"
                        icon="cil-check-alt"
                        size="lg"
                    />
                    <icon
                        v-else
                        v-c-tooltip="{
                            content: 'No attachments',
                        }"
                        icon="cil-x"
                        size="lg"
                    />
                </div>
            </td>
        </template>

        <template #actions="{ item }">
            <td class="align-middle">
                <div class="flex items-center justify-end gap-1">
                    <jet-button
                        v-if="hasAnyPermission(['task.delete'])"
                        :destructive="true"
                        :is-processing="isDeleting(item)"
                        class="btn-icon"
                        type="button"
                        variant="secondary"
                        @click.stop="deleteTask(item)"
                    >
                        <icon icon="cil-trash" />
                    </jet-button>
                </div>
            </td>
        </template>
    </data-table>
</template>
<script setup>
import { getTaskGroupColor, getTaskResolutionColor } from '@/Pages/TaskList/utils';
import TaskGroupBadge from '@/Components/TaskGroupBadge.vue';
import JetButton from '@/Jetstream/Button.vue';
import UserInformation from '@/Components/UserInformation.vue';
import DataTable from '@/Components/DataTable.vue';
import QrCode from '../../icons/QrCode.vue';
import { trans } from '@/mixins/i18n.js';
import { ref } from 'vue';
import { visitModal } from '@/Utils/visitModal';
import { router } from '@inertiajs/vue3';

const props = defineProps({
    items: Object,
    columns: { type: Array, required: true },
    queryParams: Object,
    selectionEnabled: { type: [String, Boolean], required: true },
    paginationContainer: String,
});

const emit = defineEmits(['row-clicked', 'delete-task']);

const processingItems = ref([]);
const isLoadingTaskDetails = ref(null);

const isDeleting = (item) => processingItems.value.includes(item.id);
const showTaskDetails = (task) => {
    if (!task.url) {
        return;
    }

    if (isLoadingTaskDetails.value) {
        return;
    }

    isLoadingTaskDetails.value = task.id;

    visitModal(task.url, { preserveScroll: true }).finally(() => {
        isLoadingTaskDetails.value = null;
    });
};

const deleteTask = (task) => {
    processingItems.value.push(task.id);

    router.delete(route('tasks.destroy', task.id), {
        preserveScroll: true,
        preserveState: true,
        only: ['tasks', 'errors', 'flash'],
        onFinish: () => {
            processingItems.value = processingItems.value.filter((id) => id !== task.id);
        },
    });
};

const getAirportSubareaAndArea = (item) => {
    if (!item.asset?.location) {
        return '';
    }

    // Promote the lane (level 3) into the primary slot; the grouping area (level 2) is dropped.
    const hierarchy = item.asset.location.hierarchy ?? [];
    return hierarchy.find((location) => location.level === 3)?.name ?? hierarchy.find((location) => location.level === 2)?.name ?? '';
};

const getAirportAndSpecificLocation = (item) => {
    if (!item.asset?.location) {
        return '';
    }

    const hierarchy = item.asset.location.hierarchy ?? [];
    const airport = hierarchy.find((location) => location.level === 0);
    const specificLocation = hierarchy.find((location) => location.level === 1);
    if (!airport || !specificLocation) {
        return '';
    }
    return `${specificLocation.name} (${airport.iataCode})`;
};
</script>
